import type { CSSProperties } from 'react'
import { ESPIONAGE_STAR_FILLED, ESPIONAGE_STAR_UNFILLED } from './espionageAssets'

/**
 * Espionage Page V2 — design canvas & element positions.
 *
 * Edit `x`, `y`, `width`, and `height` here to tune the layout.
 * All coordinates are in design pixels (see `ESPIONAGE_V2_DESIGN`).
 * The page stretches to the window via `computeEspionageV2PageLayout` (window size = page size).
 */

/** V2 espionage art on Vercel Blob (`land/espionage/espionage/*`). */
export const ESPIONAGE_V2_IMG_BASE =
  'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/land/espionage/espionage'

/** Design canvas matches `background.webp` natural size. */
export const ESPIONAGE_V2_DESIGN = {
  width: 1672,
  height: 940,
} as const

export interface LayoutRect {
  x: number
  y: number
  width: number
  height: number
}

export interface LayoutText extends LayoutRect {
  fontFamily: string
  fontSize: number
  fontWeight: number | string
  color: string
  textAlign?: 'left' | 'center' | 'right'
  letterSpacing?: string
  lineHeight?: number | string
  textTransform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize'
}

/** Card label (name, XP text, status) — Cinzel, positioned per card. */
export interface LayoutCardLabel extends LayoutRect {
  fontFamily: string
  fontSize: number
  fontWeight: number | string
  color: string
  textAlign?: 'left' | 'center' | 'right'
}

export type EspionageV2ImageKey =
  | 'background'
  | 'header'
  | 'headerWater'
  | 'headerWheat'
  | 'headerBread'
  | 'headerWood'
  | 'headerStone'
  | 'headerIron'
  | 'headerUsdt'
  | 'headerEcs'
  | 'icoEspionageHeader'
  | 'panelActiveSpies'
  | 'panelAvailableOperations'
  | 'panelWorldMap'
  | 'panelActiveMissions'
  | 'panelCompletedReports'
  | 'reportCard'
  | 'imgKnife'
  | 'imgScroll'

export type EspionageV2TextKey =
  | 'resourceWater'
  | 'resourceWheat'
  | 'resourceBread'
  | 'resourceWood'
  | 'resourceStone'
  | 'resourceIron'
  | 'resourceUsdt'
  | 'resourceEcs'

/** Back button hit area (not rendered as text). */
export const ESPIONAGE_V2_BACK_BUTTON: LayoutRect = {
  x: 1540,
  y: 14,
  width: 120,
  height: 40,
}

const ESPIONAGE_V2_CINZEL = '"Cinzel", "Times New Roman", serif'

export const ESPIONAGE_V2_SECTION_GOLD = '#BFA068'

export interface LayoutGradientText extends LayoutText {
  gradient: string
}

export type EspionageV2SectionTitleKey =
  | 'activeSpies'
  | 'missionReports'
  | 'availableOperations'
  | 'worldMap'
  | 'activeMissions'

/** Page header — left-center in rect; gold/bronze metallic gradient. */
export const ESPIONAGE_V2_PAGE_TITLE: LayoutGradientText = {
  x: 105,
  y: 18,
  width: 400,
  height: 33,
  fontFamily: ESPIONAGE_V2_CINZEL,
  fontSize: 28,
  fontWeight: 800,
  color: ESPIONAGE_V2_SECTION_GOLD,
  textAlign: 'left',
  gradient: 'linear-gradient(180deg, #E6D596 0%, #BFA068 48%, #6B542F 100%)',
}

/** Panel section headings — centered in each rect. */
export const ESPIONAGE_V2_SECTION_TITLES: Record<EspionageV2SectionTitleKey, LayoutText> = {
  activeSpies: {
    x: 163,
    y: 90,
    width: 172,
    height: 24,
    fontFamily: ESPIONAGE_V2_CINZEL,
    fontSize: 20,
    fontWeight: 700,
    color: ESPIONAGE_V2_SECTION_GOLD,
    textAlign: 'center',
  },
  missionReports: {
    x: 1158,
    y: 685,
    width: 203,
    height: 24,
    fontFamily: ESPIONAGE_V2_CINZEL,
    fontSize: 20,
    fontWeight: 700,
    color: ESPIONAGE_V2_SECTION_GOLD,
    textAlign: 'center',
  },
  availableOperations: {
    x: 1266,
    y: 82,
    width: 245,
    height: 24,
    fontFamily: ESPIONAGE_V2_CINZEL,
    fontSize: 20,
    fontWeight: 700,
    color: ESPIONAGE_V2_SECTION_GOLD,
    textAlign: 'center',
  },
  worldMap: {
    x: 704,
    y: 86,
    width: 222,
    height: 24,
    fontFamily: ESPIONAGE_V2_CINZEL,
    fontSize: 20,
    fontWeight: 700,
    color: ESPIONAGE_V2_SECTION_GOLD,
    textAlign: 'center',
  },
  activeMissions: {
    x: 662,
    y: 635,
    width: 210,
    height: 24,
    fontFamily: ESPIONAGE_V2_CINZEL,
    fontSize: 20,
    fontWeight: 700,
    color: ESPIONAGE_V2_SECTION_GOLD,
    textAlign: 'center',
  },
}

/**
 * Energy bar shown in the top header, immediately to the right of the
 * "Spy Operations" page title (before the resource balances begin at x≈572).
 */
export const ESPIONAGE_V2_OPERATIONS_ENERGY: LayoutRect = {
  x: 372,
  y: 12,
  width: 200,
  height: 40,
}

export const ESPIONAGE_V2_SECTION_TITLE_Z_ORDER: EspionageV2SectionTitleKey[] = [
  'activeSpies',
  'availableOperations',
  'worldMap',
  'activeMissions',
  'missionReports',
]

/** Interactive castle-only map viewport (inside world-map panel). */
export const ESPIONAGE_V2_WORLD_MAP: LayoutRect = {
  x: 485,
  y: 150,
  width: 655,
  height: 425,
}

/** Map-local decor (knife left, scroll right) — rendered inside {@link EspionageV2WorldMap}. */
export const ESPIONAGE_V2_WORLD_MAP_KNIFE: LayoutRect = {
  x: -28,
  y: 188,
  width: 58,
  height: 252,
}

export const ESPIONAGE_V2_WORLD_MAP_SCROLL: LayoutRect = {
  x: 608,
  y: 32,
  width: 108,
  height: 386,
}

/** Magnifying-glass inspect icon above a selected castle flag (canvas px, scaled by map zoom). */
export const ESPIONAGE_V2_WORLD_MAP_INSPECT_ICON = {
  size: 28,
  /** Gap between flag top and icon bottom. */
  offsetAboveFlag: 6,
} as const

/** Start mission control — top-right of world-map panel. */
export const ESPIONAGE_V2_START_MISSION_BUTTON: LayoutRect = {
  x: 712.5,
  y: 580,
  width: 200,
  height: 30,
}

export const ESPIONAGE_V2_BTN_START_MISSION = `${ESPIONAGE_V2_IMG_BASE}/btn_start_mission.webp`

/** Parchment world map drawn inside {@link EspionageV2WorldMap} (pans/zooms with castles). */
export const ESPIONAGE_V2_WORLD_MAP_BACKGROUND = `${ESPIONAGE_V2_IMG_BASE}/map.webp`

/**
 * One repeat of `map.webp` in espionage map-pixel space (before camera zoom).
 * Width is derived from the image aspect ratio at paint time.
 */
export const ESPIONAGE_V2_MAP_TILE_MAP_HEIGHT = 324

export const ESPIONAGE_V2_IMAGE_SRC: Record<EspionageV2ImageKey, string> = {
  background: `${ESPIONAGE_V2_IMG_BASE}/background.webp`,
  header: `${ESPIONAGE_V2_IMG_BASE}/header.webp`,
  headerWater: `${ESPIONAGE_V2_IMG_BASE}/header_water.webp`,
  headerWheat: `${ESPIONAGE_V2_IMG_BASE}/header_wheat.webp`,
  headerBread: `${ESPIONAGE_V2_IMG_BASE}/header_bread.webp`,
  headerWood: `${ESPIONAGE_V2_IMG_BASE}/header_wood.webp`,
  headerStone: `${ESPIONAGE_V2_IMG_BASE}/header_stone.webp`,
  headerIron: `${ESPIONAGE_V2_IMG_BASE}/header_iron.webp`,
  headerUsdt: `${ESPIONAGE_V2_IMG_BASE}/header_usdt.webp`,
  headerEcs: `${ESPIONAGE_V2_IMG_BASE}/header_ecs.webp`,
  icoEspionageHeader: `${ESPIONAGE_V2_IMG_BASE}/ico_espionage_header.webp`,
  panelActiveSpies: `${ESPIONAGE_V2_IMG_BASE}/panel_active_spies.webp`,
  panelAvailableOperations: `${ESPIONAGE_V2_IMG_BASE}/panel_available_operations.webp`,
  panelWorldMap: `${ESPIONAGE_V2_IMG_BASE}/panel_world_map.webp`,
  panelActiveMissions: `${ESPIONAGE_V2_IMG_BASE}/panel_active_missions.webp`,
  panelCompletedReports: `${ESPIONAGE_V2_IMG_BASE}/panel_completed_reports.webp`,
  reportCard: `${ESPIONAGE_V2_IMG_BASE}/report_card.webp`,
  imgKnife: `${ESPIONAGE_V2_IMG_BASE}/img_knife.webp`,
  imgScroll: `${ESPIONAGE_V2_IMG_BASE}/img_scroll.webp`,
}

/**
 * Image positions at natural asset sizes (initial layout — adjust as needed).
 */
export const ESPIONAGE_V2_IMAGES: Record<EspionageV2ImageKey, LayoutRect> = {
  background: { x: 0, y: 0, width: 1672, height: 940 },
  header: { x: 0, y: 0, width: 1672, height: 66 },
  icoEspionageHeader: { x: 53, y: 12, width: 44, height: 44 },
  headerWheat: { x: 572, y: 17, width: 117, height: 35 },
  headerBread: { x: 698, y: 17, width: 117, height: 35 },
  headerWood: { x: 815, y: 17, width: 117, height: 35 },
  headerStone: { x: 932, y: 17, width: 117, height: 35 },
  headerIron: { x: 1049, y: 17, width: 117, height: 35 },
  headerWater: { x: 1166, y: 17, width: 117, height: 35 },
  headerUsdt: { x: 1283, y: 17, width: 117, height: 35 },
  headerEcs: { x: 1400, y: 17, width: 117, height: 35 },
  panelActiveSpies: { x: 75, y: 68, width: 356, height: 788 },
  panelWorldMap: { x: 440, y: 112, width: 735, height: 500 },
  panelAvailableOperations: { x: 1191, y: 61, width: 427, height: 607 },
  panelActiveMissions: { x: 437, y: 669, width: 673, height: 257 },
  panelCompletedReports: { x: 1117, y: 669, width: 500, height: 255 },
  reportCard: { x: 1135, y: 717, width: 443, height: 63 },
  imgScroll: { x: 1095, y: 94, width: 120, height: 380 },
  imgKnife: { x: 438, y: 337, width: 63, height: 260 },
}

/** Resource amounts are filled at runtime in the page. */
export const ESPIONAGE_V2_TEXT_DEFAULTS: Record<EspionageV2TextKey, string> = {
  resourceWater: '0',
  resourceWheat: '0',
  resourceBread: '0',
  resourceWood: '0',
  resourceStone: '0',
  resourceIron: '0',
  resourceUsdt: '0.00',
  resourceEcs: '0',
}

export const ESPIONAGE_V2_TEXT: Record<EspionageV2TextKey, LayoutText> = {
  resourceWheat: {
    x: 572,
    y: 22,
    width: 110,
    height: 24,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 14,
    fontWeight: 700,
    color: '#f7e4b7',
    textAlign: 'right',
  },
  resourceBread: {
    x: 698,
    y: 22,
    width: 110,
    height: 24,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 14,
    fontWeight: 700,
    color: '#f7e4b7',
    textAlign: 'right',
  },
  resourceWood: {
    x: 815,
    y: 22,
    width: 110,
    height: 24,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 14,
    fontWeight: 700,
    color: '#f7e4b7',
    textAlign: 'right',
  },
  resourceStone: {
    x: 932,
    y: 22,
    width: 110,
    height: 24,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 14,
    fontWeight: 700,
    color: '#f7e4b7',
    textAlign: 'right',
  },
  resourceIron: {
    x: 1049,
    y: 22,
    width: 110,
    height: 24,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 14,
    fontWeight: 700,
    color: '#f7e4b7',
    textAlign: 'right',
  },
  resourceWater: {
    x: 1166,
    y: 22,
    width: 110,
    height: 24,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 14,
    fontWeight: 700,
    color: '#f7e4b7',
    textAlign: 'right',
  },
  resourceUsdt: {
    x: 1283,
    y: 22,
    width: 110,
    height: 24,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 14,
    fontWeight: 700,
    color: '#f7e4b7',
    textAlign: 'right',
  },
  resourceEcs: {
    x: 1400,
    y: 22,
    width: 110,
    height: 24,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 14,
    fontWeight: 700,
    color: '#f7e4b7',
    textAlign: 'right',
  },
}

/** Draw order (back → front). Edit to change stacking. */
export const ESPIONAGE_V2_IMAGE_Z_ORDER: EspionageV2ImageKey[] = [
  'background',
  'panelActiveSpies',
  'panelWorldMap',
  'panelAvailableOperations',
  'panelActiveMissions',
  'panelCompletedReports',
  'header',
  'headerWater',
  'headerWheat',
  'headerBread',
  'headerWood',
  'headerStone',
  'headerIron',
  'headerUsdt',
  'headerEcs',
  'icoEspionageHeader',
]

export interface EspionageV2OperationDifficultyVisual {
  color: string
  filledDots: number
  totalDots: number
  emptyDotColor: string
}

export interface EspionageV2OperationLayout {
  level: number
  difficulty: EspionageV2OperationDifficultyVisual
  iconSrc: string
  /** Icon position relative to each `available_operation_card` top-left. */
  icon: LayoutRect
}

/**
 * Available operations — four `available_operation_card.webp` rows (1 col, gap 8).
 * Icon offsets preserve former static canvas positions (1227/1226/1229 × 165/300/432/554).
 */
export const ESPIONAGE_V2_OPERATIONS_LIST = {
  cardSrc: `${ESPIONAGE_V2_IMG_BASE}/available_operation_card.webp`,
  listOrigin: { x: 1205, y: 133 },
  cardWidth: 377,
  cardHeight: 125,
  gap: 7,
  rowCount: 4,
  /**
   * Operation title (per card). First card on design canvas: (1290, 157, 210, 24).
   * Card-relative: listOrigin (1205, 137) → (85, 20, 210, 24).
   */
  title: {
    x: 85,
    y: 20,
    width: 250,
    height: 24,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 20,
    fontWeight: 500,
    color: '#3E2723',
    textAlign: 'left' as const,
  },
  /** Operation description — card-relative (80, 48, 233, 25), center-left. */
  description: {
    x: 85,
    y: 50,
    width: 233,
    height: 30,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 14,
    fontWeight: 400,
    color: '#2C241B',
    textAlign: 'left' as const,
  },
  /** Base mission duration — card-relative (124, 87, 62, 17), center-left. */
  duration: {
    x: 124,
    y: 89,
    width: 62,
    height: 17,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 14,
    fontWeight: 600,
    color: '#1A120B',
    textAlign: 'left' as const,
  },
  /** Difficulty label + dots — same row as duration, to the right. */
  difficulty: {
    x: 200,
    y: 89,
    width: 160,
    height: 17,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 12,
    fontWeight: 800,
    dotSize: 7,
    dotGap: 4,
    labelGap: 6,
  },
  /** Fantasy dark check when this operation card is selected. */
  selectionCheck: { x: 348, y: 6, width: 22, height: 22 },
  operations: [
    {
      level: 1,
      difficulty: {
        color: '#0B3F2F',
        filledDots: 1,
        totalDots: 4,
        emptyDotColor: '#2a2a2a',
      },
      iconSrc: `${ESPIONAGE_V2_IMG_BASE}/ico_aptitude_peek.webp`,
      icon: { x: 22, y: 28, width: 48, height: 50 },
    },
    {
      level: 2,
      difficulty: {
        color: '#243B5B',
        filledDots: 2,
        totalDots: 4,
        emptyDotColor: '#2a2a2a',
      },
      iconSrc: `${ESPIONAGE_V2_IMG_BASE}/ico_steal_resources.webp`,
      icon: { x: 22, y: 30, width: 47, height: 46 },
    },
    {
      level: 3,
      difficulty: {
        color: '#8B4513',
        filledDots: 3,
        totalDots: 4,
        emptyDotColor: '#2a2a2a',
      },
      iconSrc: `${ESPIONAGE_V2_IMG_BASE}/ico_decrease_loyalty.webp`,
      icon: { x: 21, y: 30, width: 47, height: 51 },
    },
    {
      level: 4,
      difficulty: {
        color: '#5C1818',
        filledDots: 4,
        totalDots: 4,
        emptyDotColor: '#2a2a2a',
      },
      iconSrc: `${ESPIONAGE_V2_IMG_BASE}/ico_kill_general.webp`,
      icon: { x: 24, y: 30, width: 40, height: 44 },
    },
  ] satisfies readonly EspionageV2OperationLayout[],
} as const

export function getEspionageV2OperationsListContentHeight(): number {
  const { cardHeight, gap, rowCount } = ESPIONAGE_V2_OPERATIONS_LIST
  return rowCount * cardHeight + (rowCount - 1) * gap
}

/**
 * Active missions — row 1, 4 columns. First card on canvas: (452, 693, 160, 200), gap 2.
 */
export const ESPIONAGE_V2_ACTIVE_MISSIONS = {
  listOrigin: { x: 452, y: 693 },
  cardWidth: 160,
  cardHeight: 200,
  gap: 2,
  columns: 4,
  cardSrcByLevel: {
    1: `${ESPIONAGE_V2_IMG_BASE}/mission_aptitude_peek_card.webp`,
    2: `${ESPIONAGE_V2_IMG_BASE}/mission_steal_resources_card.webp`,
    3: `${ESPIONAGE_V2_IMG_BASE}/mission_decrease_loyalty_card.webp`,
    4: `${ESPIONAGE_V2_IMG_BASE}/mission_kill_general_card.webp`,
  } as const,
  /** Card-relative labels — positions from design (160×200 mission card). */
  missionName: {
    x: 7,
    y: 11,
    width: 160,
    height: 20,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 15,
    fontWeight: 700,
    color: '#C49E55',
    textAlign: 'center' as const,
  },
  target: {
    x: 7,
    y: 33,
    width: 146,
    height: 18,
    fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
    fontSize: 14,
    fontWeight: 500,
    color: '#C29E65',
    textAlign: 'center' as const,
  },
  executor: {
    x: 7,
    y: 48,
    width: 146,
    height: 18,
    fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
    fontSize: 14,
    fontWeight: 600,
    color: '#C29E65',
    textAlign: 'center' as const,
  },
  remainingTimeLabel: {
    x: 7,
    y: 140,
    width: 146,
    height: 18,
    fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
    fontSize: 14,
    fontWeight: 500,
    color: '#C29E65',
    textAlign: 'center' as const,
  },
  remainingTimeValue: {
    x: 7,
    y: 158,
    width: 146,
    height: 18,
    fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
    fontSize: 14,
    fontWeight: 500,
    color: '#C29E65',
    textAlign: 'center' as const,
  },
  /** Recall CTA pinned to the bottom of each mission card — a spy can be pulled back any time. */
  recallButton: {
    x: 25,
    y: 177,
    width: 110,
    height: 18,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    borderRadius: 3,
    color: '#E8C98A',
    hoverColor: '#FFE9B5',
    background: 'rgba(28, 14, 10, 0.82)',
    hoverBackground: 'rgba(74, 24, 18, 0.92)',
    borderColor: '#8A6A32',
    hoverBorderColor: '#C49E55',
  },
} as const

export function getEspionageV2ActiveMissionsRowWidth(): number {
  const { cardWidth, gap, columns } = ESPIONAGE_V2_ACTIVE_MISSIONS
  return columns * cardWidth + (columns - 1) * gap
}

export function getEspionageV2ActiveMissionsRowHeight(): number {
  return ESPIONAGE_V2_ACTIVE_MISSIONS.cardHeight
}

/**
 * Completed mission reports — column 1, 3 visible rows, gap 0.
 * First card at legacy `reportCard` canvas position (1135, 717).
 */
export const ESPIONAGE_V2_COMPLETED_REPORTS = {
  listOrigin: { x: 1135, y: 717 },
  cardWidth: 443,
  cardHeight: 63,
  gap: 0,
  columns: 1,
  visibleRows: 3,
  cardSrc: `${ESPIONAGE_V2_IMG_BASE}/report_card.webp`,
  iconByLevel: {
    1: `${ESPIONAGE_V2_IMG_BASE}/ico_aptitude_peek.webp`,
    2: `${ESPIONAGE_V2_IMG_BASE}/ico_steal_resources.webp`,
    3: `${ESPIONAGE_V2_IMG_BASE}/ico_decrease_loyalty.webp`,
    4: `${ESPIONAGE_V2_IMG_BASE}/ico_kill_general.webp`,
  } as const,
  /** Mission-type icon on each report card. */
  icon: { x: 15, y: 16, width: 30, height: 30 },
  spyName: {
    x: 46,
    y: 10,
    width: 88,
    height: 16,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 13,
    fontWeight: 700,
    color: '#23180F ',
    textAlign: 'left' as const,
  },
  mission: {
    x: 120,
    y: 10,
    width: 160,
    height: 15,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 14,
    fontWeight: 700,
    color: '#23180F ',
    textAlign: 'center' as const,
  },
  target: {
    x: 46,
    y: 34,
    width: 208,
    height: 14,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 12,
    fontWeight: 700,
    color: '#23180F ',
    textAlign: 'left' as const,
  },
  status: {
    x: 262,
    y: 10,
    width: 90,
    height: 12,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 13,
    fontWeight: 700,
    color: '#23180F ',
    textAlign: 'right' as const,
  },
  statusColors: {
    success: '#14532d',
    failed: '#7E0723',
  },
  completedAtDate: {
    x: 355,
    y: 22,
    width: 90,
    height: 14,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 10,
    fontWeight: 400,
    color: '#CCC',
    textAlign: 'center' as const,
  },
  completedAtTime: {
    x: 355,
    y: 36,
    width: 90,
    height: 14,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 10,
    fontWeight: 400,
    color: '#CCC',
    textAlign: 'center' as const,
  },
} as const

export function getEspionageV2CompletedReportsViewportHeight(): number {
  const { cardHeight, visibleRows } = ESPIONAGE_V2_COMPLETED_REPORTS
  return visibleRows * cardHeight
}

export function getEspionageV2CompletedReportsContentHeight(itemCount: number): number {
  if (itemCount <= 0) return 0
  const { cardHeight, gap } = ESPIONAGE_V2_COMPLETED_REPORTS
  return itemCount * cardHeight + (itemCount - 1) * gap
}

export function layoutOperationTitleToStyle(
  title: (typeof ESPIONAGE_V2_OPERATIONS_LIST)['title'],
): CSSProperties {
  return layoutGeneralCardLabelToStyle(title)
}

export function layoutOperationDescriptionToStyle(
  description: (typeof ESPIONAGE_V2_OPERATIONS_LIST)['description'],
): CSSProperties {
  return {
    ...layoutGeneralCardLabelToStyle(description),
    whiteSpace: 'normal',
    lineHeight: 1.15,
    alignItems: 'center',
  }
}

export function layoutOperationDurationToStyle(
  duration: (typeof ESPIONAGE_V2_OPERATIONS_LIST)['duration'],
): CSSProperties {
  return layoutGeneralCardLabelToStyle(duration)
}

export function layoutOperationDifficultyToStyle(
  difficulty: (typeof ESPIONAGE_V2_OPERATIONS_LIST)['difficulty'],
): CSSProperties {
  return {
    position: 'absolute',
    left: difficulty.x,
    top: difficulty.y,
    width: difficulty.width,
    height: difficulty.height,
    margin: 0,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    pointerEvents: 'none',
  }
}

/** General roster — 1 column, 4-row viewport, 12px gap (scrolls when more heroes). */
export type EspionageV2ElementStamp =
  | 'fire'
  | 'water'
  | 'earth'
  | 'lightning'
  | 'magic'
  | 'physics'
  | 'sun'
  | 'dark'

export const ESPIONAGE_V2_GENERAL_CARD = {
  cardSrc: `${ESPIONAGE_V2_IMG_BASE}/character_card.webp`,
  actFrameSrc: `${ESPIONAGE_V2_IMG_BASE}/character_act_frame.webp`,
  stampSrc: {
    fire: `${ESPIONAGE_V2_IMG_BASE}/character_fire_stamp.webp`,
    water: `${ESPIONAGE_V2_IMG_BASE}/character_water_stamp.webp`,
    earth: `${ESPIONAGE_V2_IMG_BASE}/character_earth_stamp.webp`,
    lightning: `${ESPIONAGE_V2_IMG_BASE}/character_lighting_stamp.webp`,
    magic: `${ESPIONAGE_V2_IMG_BASE}/character_magic_stamp.webp`,
    physics: `${ESPIONAGE_V2_IMG_BASE}/character_physics_stamp.webp`,
    sun: `${ESPIONAGE_V2_IMG_BASE}/character_sun_stamp.webp`,
    dark: `${ESPIONAGE_V2_IMG_BASE}/character_dark_stamp.webp`,
  } satisfies Record<EspionageV2ElementStamp, string>,
  cardWidth: 330,
  cardHeight: 165,
  gap: 8,
  visibleRows: 4,
  /** List origin on the design canvas (inside active spies panel). */
  listOrigin: { x: 90, y: 147 },
  /** Positions relative to each card's top-left. */
  actFrame: { x: 23, y: 19, width: 100, height: 130 },
  /** Counter-clockwise tilt (degrees) applied to act frame + portrait together. */
  actPortraitRotateDeg: -1.3,
  stamp: { x: 255, y: 37, width: 70, height: 70 },
  portraitInset: 6,
  /**
   * Character name label (per card). First card on design canvas: (220, 185, 125, 19).
   * Card-relative: listOrigin (90, 147) → (130, 38, 125, 19).
   */
  name: {
    x: 130,
    y: 38,
    width: 125,
    height: 19,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 20,
    fontWeight: 700,
    color: '#522222',
    textAlign: 'left' as const,
  },
  /** EXP: next threshold / current xp (below name). */
  xpText: {
    x: 130,
    y: 60,
    width: 125,
    height: 12,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 11,
    fontWeight: 400,
    color: '#5c4040',
    textAlign: 'left' as const,
  },
  xpBar: {
    x: 130,
    y: 74,
    width: 125,
    height: 10,
    background: 'rgba(0, 0, 0, 0.55)',
    borderColor: 'rgba(180, 120, 50, 0.55)',
    borderWidth: 1,
    borderRadius: 2,
  },
  /** XP bar fill — dark water tone (progress within track). */
  xpBarFill: {
    highlight: '#5a8f9c',
    base: '#2d5c68',
    shadow: '#173840',
    glow: 'rgba(45, 92, 104, 0.55)',
  },
  /** 4 espionage tier stars (not 5). */
  stars: {
    x: 130,
    y: 86,
    width: 125,
    height: 18,
    count: 4,
    starSize: 18,
    gap: 3,
    filledSrc: ESPIONAGE_STAR_FILLED,
    unfilledSrc: ESPIONAGE_STAR_UNFILLED,
  },
  status: {
    x: 130,
    y: 108,
    width: 125,
    height: 20,
    fontFamily: '"Cinzel", "Times New Roman", serif',
    fontSize: 13,
    fontWeight: 600,
    textAlign: 'left' as const,
  },
  statusColors: {
    ultimate: '#540c5f',
    training: '#2fa7ff',
    onMission: '#7000a0',
    trained: '#75d94e',
    default: '#05792e',
    /** Spy that has the skills but is tied up elsewhere (mining, battle, envoy, …). */
    busy: '#6b6257',
  },
  /** Spy tier at which XP bar shows MAX and all stars are filled. */
  maxEspionageTier: 4,
  /** Fantasy dark check when this general card is selected. */
  selectionCheck: { x: 298, y: 6, width: 22, height: 22 },
} as const

export function layoutGeneralCardLabelToStyle(label: LayoutCardLabel): CSSProperties {
  const justifyMap = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
  } as const
  return {
    position: 'absolute',
    left: label.x,
    top: label.y,
    width: label.width,
    height: label.height,
    margin: 0,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: justifyMap[label.textAlign ?? 'left'],
    fontFamily: label.fontFamily,
    fontSize: label.fontSize,
    fontWeight: label.fontWeight,
    color: label.color,
    textAlign: label.textAlign,
    lineHeight: 1,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    pointerEvents: 'none',
  }
}

export function layoutSectionTitleToStyle(title: LayoutText): CSSProperties {
  return {
    ...layoutGeneralCardLabelToStyle(title),
    zIndex: 22,
  }
}

export function layoutGradientTitleToStyle(title: LayoutGradientText): CSSProperties {
  return {
    ...layoutSectionTitleToStyle(title),
    background: title.gradient,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    color: 'transparent',
    filter: 'drop-shadow(0 1px 0 rgba(82, 62, 30, 0.55))',
  }
}

export function layoutGeneralNameToStyle(
  name: (typeof ESPIONAGE_V2_GENERAL_CARD)['name'],
): CSSProperties {
  return layoutGeneralCardLabelToStyle(name)
}

/** Portrait treatment inside the act frame — light worn tint. */
export const ESPIONAGE_V2_PORTRAIT_MASK = {
  imageFilter: 'brightness(0.9) contrast(1.05) saturate(0.82) sepia(0.18)',
  vignetteBackground:
    'radial-gradient(ellipse 90% 100% at 50% 35%, rgba(18, 14, 10, 0) 0%, rgba(10, 8, 5, 0.18) 55%, rgba(4, 3, 2, 0.42) 100%)',
  wearBackground:
    'linear-gradient(165deg, rgba(48, 32, 18, 0.22) 0%, rgba(16, 12, 8, 0.08) 38%, rgba(32, 22, 12, 0.18) 72%, rgba(8, 6, 4, 0.24) 100%)',
  scratchBackground:
    'repeating-linear-gradient(-24deg, transparent 0px, transparent 3px, rgba(0, 0, 0, 0.025) 3px, rgba(0, 0, 0, 0.025) 4px)',
  grainOpacity: 0.06,
  selectedImageFilter: 'brightness(0.96) contrast(1.03) saturate(0.86) sepia(0.14)',
} as const

export function getEspionageV2GeneralListViewportHeight(): number {
  const { cardHeight, gap, visibleRows } = ESPIONAGE_V2_GENERAL_CARD
  return visibleRows * cardHeight + (visibleRows - 1) * gap
}

export function getEspionageV2GeneralListContentHeight(itemCount: number): number {
  if (itemCount <= 0) return 0
  const { cardHeight, gap } = ESPIONAGE_V2_GENERAL_CARD
  return itemCount * cardHeight + (itemCount - 1) * gap
}

export const ESPIONAGE_V2_TEXT_Z_ORDER: EspionageV2TextKey[] = [
  'resourceWater',
  'resourceWheat',
  'resourceBread',
  'resourceWood',
  'resourceStone',
  'resourceIron',
  'resourceUsdt',
  'resourceEcs',
]

export interface EspionageV2PageLayout {
  /** Horizontal scale — visual width equals viewport width. */
  scaleX: number
  /** Vertical scale — visual height equals viewport height. */
  scaleY: number
  /** Average scale (world map touch / legacy helpers). */
  scale: number
  scaledWidth: number
  scaledHeight: number
}

/**
 * Stretch the 1672×940 design to exactly fill the window (width and height match).
 */
export function computeEspionageV2PageLayout(
  viewportW: number,
  viewportH: number,
): EspionageV2PageLayout {
  const w = Math.max(1, viewportW)
  const h = Math.max(1, viewportH)
  const scaleX = w / ESPIONAGE_V2_DESIGN.width
  const scaleY = h / ESPIONAGE_V2_DESIGN.height
  return {
    scaleX,
    scaleY,
    scale: (scaleX + scaleY) / 2,
    scaledWidth: w,
    scaledHeight: h,
  }
}

export function getEspionageV2Scale(viewportW: number, viewportH: number): number {
  return computeEspionageV2PageLayout(viewportW, viewportH).scale
}

export function scaleLayoutRect(rect: LayoutRect, scale: number): LayoutRect {
  return {
    x: rect.x * scale,
    y: rect.y * scale,
    width: rect.width * scale,
    height: rect.height * scale,
  }
}

export function layoutRectToStyle(rect: LayoutRect): CSSProperties {
  return {
    position: 'absolute',
    left: rect.x,
    top: rect.y,
    width: rect.width,
    height: rect.height,
  }
}

export function layoutTextToStyle(text: LayoutText): CSSProperties {
  return {
    ...layoutRectToStyle(text),
    fontFamily: text.fontFamily,
    fontSize: text.fontSize,
    fontWeight: text.fontWeight,
    color: text.color,
    textAlign: text.textAlign,
    letterSpacing: text.letterSpacing,
    lineHeight: text.lineHeight,
    textTransform: text.textTransform,
    margin: 0,
    padding: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    pointerEvents: 'none',
  }
}
